import { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../api/axios';
import { AuthContext } from '../context/AuthContext'; // THE FIX IS HERE: Use named import

const ProfileForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditing = Boolean(id);
  const { user } = useContext(AuthContext);

  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    content: {
      display_name: '',
      description: '',
      facebook_url: '',
      twitter_url: '',
      linkedin_url: '',
      instagram_url: '',
    }
  });

  const returnPath = user?.role === 'super_admin' ? '/crm/leads/profiles' : '/';

  useEffect(() => {
    if (isEditing) {
      const fetchProfile = async () => {
        try {
          const fetchEndpoint = user?.role === 'super_admin' ? `/crm/profile/${id}` : `/user/profile/${id}`;
          const response = await api.get(fetchEndpoint);
          const { name, slug, content } = response.data;
          setFormData(prev => ({ ...prev, name, slug, content: { ...prev.content, ...content } }));
        } catch (error) {
          console.error("Failed to fetch profile", error);
          navigate(returnPath);
        }
      };
      fetchProfile();
    }
  }, [id, isEditing, navigate, returnPath, user]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name in formData.content) {
      setFormData(prev => ({ ...prev, content: { ...prev.content, [name]: value } }));
    } else {
      let processedValue = value;
      if (name === 'slug') {
          processedValue = value.toLowerCase().replace(/[^a-z0-9-]+/g, '');
      }
      setFormData(prev => ({ ...prev, [name]: processedValue }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const isSuperAdmin = user && user.role === 'super_admin';
    const createEndpoint = isSuperAdmin ? '/crm/profile/create_lead' : '/user/profile/create';
    const updateEndpoint = isSuperAdmin ? `/crm/profile/update/${id}` : `/user/profile/update/${id}`;
    
    const payload = { ...formData };
    
    try {
      if (isEditing) {
        await api.put(updateEndpoint, payload);
      } else {
        await api.post(createEndpoint, payload);
      }
      navigate(returnPath);
    } catch (error) {
      console.error("Failed to save profile", error);
      alert(error.response?.data?.message || "An error occurred.");
    }
  };

  return (
    <div className="max-w-2xl mx-auto py-8">
      <h1 className="text-3xl font-bold mb-6">{isEditing ? 'Edit Profile' : 'Create New Profile'}</h1>
      <form onSubmit={handleSubmit} className="bg-white shadow-md rounded px-8 pt-6 pb-8 mb-4 space-y-6">
        <div>
          <label className="block text-sm font-medium text-gray-700">Full Name *</label>
          <input type="text" name="name" value={formData.name} onChange={handleChange} required className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-orange-500 focus:ring-orange-500" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">Username / Slug *</label>
          <input type="text" name="slug" value={formData.slug} onChange={handleChange} required disabled={isEditing} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm disabled:bg-gray-100 disabled:cursor-not-allowed" />
          <p className="text-xs text-gray-500 mt-1">Unique URL-friendly name. Cannot be changed later.</p>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">Display Name</label>
          <input type="text" name="display_name" value={formData.content.display_name} onChange={handleChange} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-orange-500 focus:ring-orange-500" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">Description / Bio</label>
          <textarea name="description" value={formData.content.description} onChange={handleChange} rows="4" className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-orange-500 focus:ring-orange-500"></textarea>
        </div>
        <h3 className="text-lg font-semibold border-t pt-6">Social Links</h3>
        <div>
          <label className="block text-sm font-medium text-gray-700">Facebook URL</label>
          <input type="url" name="facebook_url" value={formData.content.facebook_url} onChange={handleChange} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-orange-500 focus:ring-orange-500" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">Twitter URL</label>
          <input type="url" name="twitter_url" value={formData.content.twitter_url} onChange={handleChange} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-orange-500 focus:ring-orange-500" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">LinkedIn URL</label>
          <input type="url" name="linkedin_url" value={formData.content.linkedin_url} onChange={handleChange} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-orange-500 focus:ring-orange-500" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">Instagram URL</label>
          <input type="url" name="instagram_url" value={formData.content.instagram_url} onChange={handleChange} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-orange-500 focus:ring-orange-500" />
        </div>
        <div className="flex justify-end gap-4 pt-4">
          <button type="button" onClick={() => navigate(returnPath)} className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium hover:bg-gray-50">Cancel</button>
          <button type="submit" className="px-4 py-2 bg-orange-600 text-white rounded-md text-sm font-medium hover:bg-orange-700">Save Profile</button>
        </div>
      </form>
    </div>
  );
};

export default ProfileForm;